import { useState, useEffect, useCallback } from "react";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CalendarBlank, CaretLeft, CaretRight, ShieldWarning, ShieldCheck } from "@phosphor-icons/react";
import { toLocalISODate } from "../lib/dateUtil";
import { fmtDate } from "../lib/dateUtil";

export default function DocumentExpiryCalendarPage() {
  const [docs, setDocs] = useState([]);
  const [maint, setMaint] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("month");

  useEffect(() => {
    Promise.all([
      API.get("/analytics/document-expiry-calendar").then(({ data }) => setDocs(data)).catch(() => {}),
      API.get("/analytics/maintenance-calendar").then(({ data }) => setMaint(Array.isArray(data) ? data : [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const monthName = currentDate.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const getDocsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return docs.filter(d => d.expiry_date?.startsWith(dateStr));
  };

  const getMaintForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return maint.filter(m => m.next_due === dateStr);
  };

  const today = new Date();
  const isToday = (day) => today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
  const isPast = (day) => new Date(year, month, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());

  // List view
  const sortedDocs = [...docs].sort((a, b) => (a.expiry_date || "").localeCompare(b.expiry_date || ""));
  const todayISO = toLocalISODate(today);
  const upcomingDocs = sortedDocs.filter(d => d.expiry_date >= todayISO);
  const expiredDocs = sortedDocs.filter(d => d.expiry_date < todayISO);

  return (
    <div data-testid="document-expiry-calendar-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Compliance Calendar</h1>
          <p className="text-sm text-muted-foreground mt-1">{docs.length} compliance documents • {maint.length} planned maintenance job{maint.length !== 1 ? "s" : ""}</p>
        </div>
        <Select value={view} onValueChange={setView}>
          <SelectTrigger className="w-[120px]" data-testid="calendar-view-toggle">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Calendar</SelectItem>
            <SelectItem value="list">List</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
      ) : view === "list" ? (
        <div className="space-y-6">
          {maint.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-amber-600 dark:text-amber-400 mb-2 flex items-center gap-1"><CalendarBlank size={16} /> Planned Maintenance ({maint.length})</h3>
              <div className="border border-amber-200 dark:border-amber-800 rounded-md divide-y divide-amber-100 dark:divide-amber-900" data-testid="maintenance-list">
                {maint.map(m => {
                  const daysLeft = Math.ceil((new Date(m.next_due) - today) / 86400000);
                  return (
                    <div key={m.id} className="px-4 py-3 flex items-center justify-between" data-testid={`maint-row-${m.id}`}>
                      <div>
                        <p className="text-sm font-medium">{m.title || m.reference}</p>
                        <p className="text-xs text-muted-foreground">{m.reference}{m.client_name ? ` • ${m.client_name}` : ""} • repeats {m.frequency}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-mono">{m.next_due}</p>
                        <Badge variant={daysLeft <= 0 ? "destructive" : daysLeft <= 14 ? "secondary" : "outline"} className="text-[9px]">
                          {daysLeft <= 0 ? "due" : `due in ${daysLeft}d`}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {expiredDocs.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-2 flex items-center gap-1"><ShieldWarning size={16} /> Expired ({expiredDocs.length})</h3>
              <div className="border border-red-200 dark:border-red-800 rounded-md divide-y divide-red-100 dark:divide-red-900">
                {expiredDocs.map(d => (
                  <div key={d.id} className="px-4 py-3 flex items-center justify-between" data-testid={`expired-doc-${d.id}`}>
                    <div>
                      <p className="text-sm font-medium">{d.doc_type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</p>
                      <p className="text-xs text-muted-foreground">{d.vendor_name}</p>
                    </div>
                    <Badge variant="destructive" className="text-[10px]">{fmtDate(d.expiry_date)}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
          {upcomingDocs.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-1"><CalendarBlank size={16} /> Upcoming ({upcomingDocs.length})</h3>
              <div className="border border-border rounded-md divide-y divide-border">
                {upcomingDocs.map(d => {
                  const daysLeft = Math.ceil((new Date(d.expiry_date) - today) / 86400000);
                  return (
                    <div key={d.id} className="px-4 py-3 flex items-center justify-between" data-testid={`upcoming-doc-${d.id}`}>
                      <div>
                        <p className="text-sm font-medium">{d.doc_type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</p>
                        <p className="text-xs text-muted-foreground">{d.vendor_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-mono">{fmtDate(d.expiry_date)}</p>
                        <Badge variant={daysLeft <= 30 ? "destructive" : daysLeft <= 60 ? "secondary" : "outline"} className="text-[9px]">
                          {daysLeft}d left
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="sm" onClick={prevMonth} data-testid="cal-prev"><CaretLeft size={16} /></Button>
            <h2 className="text-base font-semibold font-[Chivo]">{monthName}</h2>
            <Button variant="outline" size="sm" onClick={nextMonth} data-testid="cal-next"><CaretRight size={16} /></Button>
          </div>
          <div className="grid grid-cols-7 gap-px border border-border rounded-md overflow-hidden bg-border" data-testid="calendar-grid">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
              <div key={day} className="bg-muted px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{day}</div>
            ))}
            {Array.from({ length: firstDay }, (_, i) => (
              <div key={`empty-${i}`} className="bg-card min-h-[80px]" />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const dayDocs = getDocsForDay(day);
              const dayMaint = getMaintForDay(day);
              const past = isPast(day) && !isToday(day);
              return (
                <div
                  key={day}
                  className={`bg-card min-h-[80px] px-1.5 py-1 transition-colors ${isToday(day) ? "ring-2 ring-primary ring-inset" : ""} ${past ? "opacity-50" : ""}`}
                  data-testid={`cal-day-${day}`}
                >
                  <span className={`text-xs font-medium ${isToday(day) ? "text-primary font-bold" : "text-muted-foreground"}`}>{day}</span>
                  <div className="mt-0.5 space-y-0.5">
                    {dayDocs.slice(0, 2).map(d => (
                      <div key={d.id} className={`text-[9px] px-1 py-0.5 rounded truncate ${d.verified ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400" : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"}`} title={`${d.doc_type} - ${d.vendor_name}`}>
                        {d.vendor_name?.slice(0, 12)}
                      </div>
                    ))}
                    {dayMaint.slice(0, 2).map(m => (
                      <div key={m.id} className="text-[9px] px-1 py-0.5 rounded truncate bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400" title={`Planned maintenance: ${m.title || m.reference} (${m.frequency})`} data-testid={`maint-chip-${m.id}`}>
                        {(m.title || m.reference)?.slice(0, 14)}
                      </div>
                    ))}
                    {dayDocs.length + dayMaint.length > 4 && <span className="text-[8px] text-muted-foreground">+{dayDocs.length + dayMaint.length - 4} more</span>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-3 text-[10px]">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded bg-emerald-500" /> Verified</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded bg-red-500" /> Unverified</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded bg-amber-500" /> Planned maintenance</span>
          </div>
        </div>
      )}
    </div>
  );
}

